import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useCallback, useState } from "react";

import { cn } from "@/lib/chillnn/cn";

import type { SnackBlocksFormModelContentsMethodsType } from "@/components/features/snack/snack/settings/model/usecase/use-create-snack-layout-settings-form-model/methods/contents";

import { Button } from "@/components/ui/button/button";
import Delete from "@/components/ui/icon/delete";
import DragIndicator from "@/components/ui/icon/drag-indicator";
import EditDocument from "@/components/ui/icon/edit-document";

import { LayoutSettingsIslandContentRowSnackItemRoot } from "./snack-item";
import { DialogBlockTitleSettings } from "../../dialog/block-title";
import { DialogSnackLayoutSettings } from "../../dialog/layout";
import { SnackLayoutSettingsDialogDetailSelectLayout } from "../../dialog/layout/select-layout";

type Props = {
	hotelID: string;
	index: number;

	block: SnackBlocksFormModelContentsMethodsType["blocks"]["value"][number];

	contentsMethods: SnackBlocksFormModelContentsMethodsType;
};

export const LayoutSettingsIslandContentRow = ({ hotelID, index, block, contentsMethods }: Props) => {
	const { onDeleteBlock, onSetBlockTitle, onSetBlockLayoutType } = contentsMethods;

	const { setNodeRef, setActivatorNodeRef, listeners, attributes, transform, isDragging } = useSortable({
		id: block.uuid.value,
	});

	// Blockの個数を選択するモーダルの表示状態
	const [isShowSelectBlockCountModal, setIsShowSelectBlockCountModal] = useState<boolean>(false);
	const activateSelectBlockCountModal = useCallback(() => {
		setIsShowSelectBlockCountModal(true);
	}, [setIsShowSelectBlockCountModal]);
	const deactivateSelectBlockCountModal = useCallback(() => {
		setIsShowSelectBlockCountModal(false);
	}, [setIsShowSelectBlockCountModal]);
	const handleOpenChangeSelectBlockCountModal = useCallback(
		(input: boolean) => {
			setIsShowSelectBlockCountModal(input);
		},
		[setIsShowSelectBlockCountModal],
	);

	// Blockのタイトルを編集するモーダルの表示状態
	const [isShowInputBlockTitleModal, setIsShowInputBlockTitleModal] = useState<boolean>(false);
	const activateInputBlockTitleModal = useCallback(() => {
		setIsShowInputBlockTitleModal(true);
	}, [setIsShowInputBlockTitleModal]);
	const deactivateInputBlockTitleModal = useCallback(() => {
		setIsShowInputBlockTitleModal(false);
	}, [setIsShowInputBlockTitleModal]);
	const handleOpenChangeInputBlockTitleModal = useCallback(
		(input: boolean) => {
			setIsShowInputBlockTitleModal(input);
		},
		[setIsShowInputBlockTitleModal],
	);

	const { items, title, uuid } = block;
	const itemLength = items.value.length;
	const gridColsTailwindStyle = itemLength === 1 ? "grid-cols-1" : itemLength === 2 ? "grid-cols-2" : "grid-cols-3";
	const gapXTailwindStyle = itemLength === 1 ? "gap-x-0" : itemLength === 2 ? "gap-x-20" : "gap-x-5";

	const handleClickEditButton = useCallback(() => {
		activateSelectBlockCountModal();
	}, [activateSelectBlockCountModal]);
	const handleClickDeleteButton = useCallback(() => {
		if (window.confirm("このブロックを削除しますか？")) {
			onDeleteBlock({ targetUUID: uuid.value });
		}
	}, [onDeleteBlock, uuid.value]);

	return (
		<div
			ref={setNodeRef}
			style={{
				transform: CSS.Transform.toString(transform),
			}}
			className="relative space-y-2"
		>
			<div className="flex justify-end gap-x-2">
				<Button
					type="button"
					size="icon"
					variant="input-ish"
					tooltip="レイアウト変更"
					onClick={handleClickEditButton}
				>
					<EditDocument />
				</Button>
				<Button type="button" size="icon" variant="input-ish" tooltip="削除" onClick={handleClickDeleteButton}>
					<Delete />
				</Button>
			</div>

			{/* ブロックのタイトル */}
			<div className="relative mb-5 w-full border-b border-b-white pb-[5px] pt-3 text-center">
				<p className="line-clamp-1 text-lg font-semibold text-white">{title?.value || "未設定"}</p>
				<Button
					className="absolute right-0 top-0"
					type="button"
					size="icon"
					variant="input-ish"
					tooltip="タイトルの編集"
					onClick={activateInputBlockTitleModal}
				>
					<EditDocument />
				</Button>
			</div>

			<DialogBlockTitleSettings
				open={isShowInputBlockTitleModal}
				handleOpenChange={handleOpenChangeInputBlockTitleModal}
				handleClickCloseButton={deactivateInputBlockTitleModal}
				onSetBlockTitle={({ title }: { title: string }) => onSetBlockTitle({ title, index })}
				title={block.title}
			/>

			<div className={cn("grid items-center", gridColsTailwindStyle, gapXTailwindStyle)}>
				{items.value.map((item, idx) => {
					return (
						<LayoutSettingsIslandContentRowSnackItemRoot
							key={`${uuid.value}_${idx}`}
							index={idx}
							hotelID={hotelID}
							itemLength={itemLength}
							blockUUID={uuid.value}
							snack={item}
							contentsMethods={contentsMethods}
						/>
					);
				})}
			</div>

			<DialogSnackLayoutSettings
				open={isShowSelectBlockCountModal}
				handleOpenChange={handleOpenChangeSelectBlockCountModal}
				handleClickCloseButton={deactivateSelectBlockCountModal}
			>
				<SnackLayoutSettingsDialogDetailSelectLayout
					onSetBlockLayoutType={onSetBlockLayoutType}
					uuid={block.uuid}
				/>
			</DialogSnackLayoutSettings>

			<div ref={setActivatorNodeRef} {...attributes} {...listeners} className="absolute -right-10 top-1/2">
				<DragIndicator className={cn(isDragging ? "cursor-grabbing" : "cursor-grab")} />
			</div>
		</div>
	);
};

LayoutSettingsIslandContentRow.displayName = "LayoutSettingsIslandContentRow";
